/**
 * TournamentsSection - Tournament display for Hub pages
 *
 * Clean tournament page following MEL design pattern:
 * - Hero with title left, info card right
 * - Simple tab navigation
 * - Clean bracket display
 *
 * Fetches real tournament data from the hub competitions endpoint,
 * then fetches detailed bracket/teams from the tournament detail API.
 */

import { ReactElement, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaTrophy, FaCalendarAlt, FaGamepad, FaChevronLeft, FaChevronRight, FaUsers } from "react-icons/fa";
import { useGetHubCompetitions, HubCompetition } from "@/hooks/hub/useGetHubCompetitions";
import {TournamentsApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import Skeleton from "react-loading-skeleton";
import "./TournamentsSection.scss";

interface TournamentsSectionProps {
    /** Hub slug for fetching competitions */
    surfaceSlug?: string;
}

/*
 * ============================================================================
 * API Response Interfaces
 * ============================================================================
 */

interface TournamentTeamResponse {
    id: string;
    name: string;
    tag?: string;
    seed?: number;
    rosterCount: number;
}

interface TournamentMatchResponse {
    id: string;
    matchId: string;
    roundNumber: number;
    team1Id?: string;
    team2Id?: string;
    team1Score?: number;
    team2Score?: number;
    winnerId?: string;
    status: string;
    scheduledTime?: number;
    completedAt?: number;
}

interface TournamentDetailResponse {
    id: string;
    name: string;
    bracketType?: string;
    status: string;
    teamSize?: number;
    maxTeams?: number;
    registeredTeamsCount?: number;
    teams?: TournamentTeamResponse[];
    matches?: TournamentMatchResponse[];
}

/*
 * ============================================================================
 * API Fetch Functions
 * ============================================================================
 */

async function fetchTournamentDetails(tournamentId: string): Promise<TournamentDetailResponse> {
    const api = new TournamentsApi(getConfig());
    return api.getTournamentsId({id: tournamentId}) as unknown as Promise<TournamentDetailResponse>;
}

/**
 * Format a timestamp to a readable date range
 */
function formatTournamentDate(startDate?: number, endDate?: number): string {
    if (!startDate) return "TBD";

    const start = new Date(startDate);
    const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };

    if (!endDate || startDate === endDate) {
        return start.toLocaleDateString("en-US", options);
    }

    const end = new Date(endDate);
    // Same month - show "February 15-16, 2026"
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    }

    // Different months
    return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", options)}`;
}

/**
 * Format prize pool for display
 */
function formatPrizePool(prizePool?: HubCompetition["prizePool"]): string {
    if (!prizePool?.total) return "TBD";

    const amount = prizePool.total;
    const currency = prizePool.currency || "USD";

    if (currency === "USD") {
        return amount >= 1000
            ? `$${(amount / 100).toLocaleString()}+`
            : `$${(amount / 100).toFixed(0)}`;
    }

    return `${amount.toLocaleString()} ${currency}`;
}

/**
 * Get round name based on position from finals
 * e.g., totalRounds=3: round 1="Quarter-Finals", round 2="Semi-Finals", round 3="Finals"
 */
function getRoundName(roundNumber: number, totalRounds: number): string {
    const roundsFromEnd = totalRounds - roundNumber;

    switch (roundsFromEnd) {
        case 0:
            return "Finals";
        case 1:
            return "Semi-Finals";
        case 2:
            return "Quarter-Finals";
        case 3:
            return "Round of 16";
        case 4:
            return "Round of 32";
        case 5:
            return "Round of 64";
        default:
            return `Round ${roundNumber}`;
    }
}

/**
 * Build bracket structure from matches array
 */
interface BracketMatch {
    id: string;
    team1: { id?: string; name: string; seed?: number } | null;
    team2: { id?: string; name: string; seed?: number } | null;
    score1: number | null;
    score2: number | null;
    winnerId?: string;
    status: string;
    scheduledTime?: number;
}

interface BracketRound {
    roundNumber: number;
    name: string;
    matches: BracketMatch[];
}

function buildBracketFromMatches(
    matches: TournamentMatchResponse[],
    teams: TournamentTeamResponse[],
): BracketRound[] {
    if (!matches || matches.length === 0) {
        return [];
    }

    // Create team lookup map
    const teamMap = new Map<string, TournamentTeamResponse>();
    teams.forEach((team) => teamMap.set(team.id, team));

    // Group matches by round
    const matchesByRound = new Map<number, TournamentMatchResponse[]>();
    let maxRound = 0;

    matches.forEach((match) => {
        const round = match.roundNumber;
        if (!matchesByRound.has(round)) {
            matchesByRound.set(round, []);
        }
        matchesByRound.get(round)!.push(match);
        maxRound = Math.max(maxRound, round);
    });

    // Convert to bracket rounds
    const rounds: BracketRound[] = [];

    for (let roundNum = 1; roundNum <= maxRound; roundNum++) {
        const roundMatches = matchesByRound.get(roundNum) || [];

        rounds.push({
            roundNumber: roundNum,
            name: getRoundName(roundNum, maxRound),
            matches: roundMatches.map((m) => {
                const team1 = m.team1Id ? teamMap.get(m.team1Id) : null;
                const team2 = m.team2Id ? teamMap.get(m.team2Id) : null;

                return {
                    id: m.id,
                    team1: team1 ? { id: team1.id, name: team1.name, seed: team1.seed } : null,
                    team2: team2 ? { id: team2.id, name: team2.name, seed: team2.seed } : null,
                    score1: m.team1Score ?? null,
                    score2: m.team2Score ?? null,
                    winnerId: m.winnerId,
                    status: m.status,
                    scheduledTime: m.scheduledTime,
                };
            }),
        });
    }

    return rounds;
}

export const TournamentsSection = ({
    surfaceSlug,
}: TournamentsSectionProps): ReactElement => {
    const [activeTab, setActiveTab] = useState<"bracket" | "teams" | "rules" | "schedule">("bracket");

    // Fetch tournaments for this hub
    const { data: competitionsData, isLoading: isLoadingTournaments } = useGetHubCompetitions(surfaceSlug, {
        type: "TOURNAMENT",
    });

    // Get the first (featured) tournament, or use demo data as fallback
    const tournaments = competitionsData?.competitions || [];
    const featuredTournament = tournaments[0];
    const tournamentId = featuredTournament?.id;

    // Fetch detailed tournament data (teams, matches) when we have an ID
    const { data: tournamentDetails, isLoading: isLoadingDetails, error: detailsError } = useQuery({
        queryKey: ["tournament-details", tournamentId],
        queryFn: () => fetchTournamentDetails(tournamentId!),
        enabled: Boolean(tournamentId),
        staleTime: 60 * 1000, // 1 minute
        retry: 2,
    });

    // Log errors for debugging (could be replaced with toast notification)
    if (detailsError) {
        console.error("Failed to load tournament details:", detailsError);
    }

    // Use real teams or fall back to demo
    const teams = tournamentDetails?.teams || [];
    const matches = tournamentDetails?.matches || [];
    const useRealMatches = matches.length > 0;

    // Build bracket from real match data
    const bracketRounds = useMemo(() => {
        if (matches.length === 0) return [];
        return buildBracketFromMatches(matches, teams);
    }, [matches, teams]);

    // Determine the champion (winner of the final match)
    const champion = useMemo(() => {
        if (bracketRounds.length === 0) return null;
        const finalRound = bracketRounds[bracketRounds.length - 1];
        const finalMatch = finalRound?.matches[0];
        if (finalMatch?.winnerId) {
            const winnerTeam = teams.find((t) => t.id === finalMatch.winnerId);
            return winnerTeam || null;
        }
        return null;
    }, [bracketRounds, teams]);

    // Derive display values from real data or fallback to demo
    const displayData = useMemo(() => {
        if (!featuredTournament) {
            /*
             * Never rendered — the component early-returns an empty state when there
             * is no tournament. Returned only so the memo keeps a value of this shape.
             */
            return {name: "", game: "", mode: "", date: "", platform: "", region: "", prizePool: "", teamSize: 0, maxTeams: 0, registeredTeams: 0, format: ""};
        }

        return {
            name: featuredTournament.name || "TOURNAMENT",
            game: featuredTournament.game?.name || "Game TBD",
            mode: `${featuredTournament.teamSize || 1}v${featuredTournament.teamSize || 1}`,
            date: formatTournamentDate(featuredTournament.startDate, featuredTournament.endDate),
            platform: "Online",
            region: "Global",
            prizePool: formatPrizePool(featuredTournament.prizePool),
            teamSize: featuredTournament.teamSize || 1,
            maxTeams: featuredTournament.maxParticipants || 32,
            registeredTeams: tournamentDetails?.registeredTeamsCount || featuredTournament.participantCount || teams.length || 0,
            format: tournamentDetails?.bracketType || "Single Elimination",
        };
    }, [featuredTournament, tournamentDetails, teams.length]);

    const isLoading = isLoadingTournaments || !!tournamentId && isLoadingDetails;

    // Loading state
    if (isLoading) {
        return (
            <section className="tournaments-section">
                <div className="tournaments-section__info-bar">
                    <Skeleton width={150} height={20} />
                    <Skeleton width={150} height={20} />
                    <Skeleton width={150} height={20} />
                </div>
                <div className="tournaments-section__tabs">
                    <Skeleton width={400} height={40} />
                </div>
            </section>
        );
    }

    // No real tournament — empty state instead of fabricated demo teams/bracket.
    if (!featuredTournament) {
        return (
            <section className="tournaments-section">
                <div className="tournaments-section__empty">
                    <FaTrophy size={28} />
                    <h3 className="tournaments-section__empty-title">No active tournaments</h3>
                    <p className="tournaments-section__empty-sub">There are no tournaments running here right now — check back soon.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="tournaments-section">
            {/* Info Bar - Compact horizontal bar */}
            <div className="tournaments-section__info-bar">
                <div className="tournaments-section__info-item">
                    <FaGamepad size={14} />
                    <span>{displayData.game}</span>
                </div>
                <div className="tournaments-section__info-item">
                    <FaCalendarAlt size={14} />
                    <span>{displayData.date}</span>
                </div>
                <div className="tournaments-section__info-item">
                    <FaUsers size={14} />
                    <span>{displayData.registeredTeams}/{displayData.maxTeams} Teams</span>
                </div>
                <div className="tournaments-section__info-item tournaments-section__info-item--prize">
                    <FaTrophy size={14} />
                    <span>{displayData.prizePool} Prize Pool</span>
                </div>
                <div className="tournaments-section__info-actions">
                    <button className="tournaments-section__btn tournaments-section__btn--primary">
                        REGISTER YOUR TEAM
                    </button>
                </div>
            </div>

            {/* Tab Navigation - Simple underline style */}
            <div className="tournaments-section__tabs">
                <button
                    className={`tournaments-section__tab ${activeTab === "bracket" ? "tournaments-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("bracket")}
                >
                    <FaTrophy size={14} /> TOURNAMENT
                </button>
                <button
                    className={`tournaments-section__tab ${activeTab === "teams" ? "tournaments-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("teams")}
                >
                    TEAMS ({displayData.registeredTeams})
                </button>
                <button
                    className={`tournaments-section__tab ${activeTab === "schedule" ? "tournaments-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("schedule")}
                >
                    SCHEDULE
                </button>
                <button
                    className={`tournaments-section__tab ${activeTab === "rules" ? "tournaments-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("rules")}
                >
                    INFO
                </button>
            </div>

            {/* Bracket Content */}
            {activeTab === "bracket" &&
                <div className="tournaments-section__bracket-section">
                    <div className="tournaments-section__bracket-header">
                        <h2 className="tournaments-section__bracket-title">TOURNAMENT BRACKETS</h2>
                        <p className="tournaments-section__bracket-subtitle">
                            Championship Finals - {displayData.date}
                        </p>
                    </div>

                    <div className="tournaments-section__division-bar">
                        <span className="tournaments-section__division-name">OPEN DIVISION</span>
                        <span className="tournaments-section__division-format">{displayData.format}</span>
                    </div>

                    {/* Dynamic Bracket Grid - Real Data */}
                    {useRealMatches && bracketRounds.length > 0
                        ? <div className="tournaments-section__bracket-grid" style={{ "--bracket-columns": bracketRounds.length + 1 } as React.CSSProperties}>
                            {/* Column Headers */}
                            <div className="tournaments-section__bracket-columns">
                                {bracketRounds.map((round) =>
                                    <div key={round.roundNumber} className="tournaments-section__bracket-col-header">
                                        {round.name.toUpperCase()}
                                    </div>)}
                                <div className="tournaments-section__bracket-col-header tournaments-section__bracket-col-header--champion">
                                    <FaTrophy size={12} /> CHAMPION
                                </div>
                            </div>

                            {/* Bracket Matches */}
                            <div className="tournaments-section__bracket-content">
                                {bracketRounds.map((round) =>
                                    <div key={round.roundNumber} className="tournaments-section__bracket-column">
                                        {round.matches.map((match) => {
                                            const team1IsWinner = match.winnerId && match.team1?.id === match.winnerId;
                                            const team2IsWinner = match.winnerId && match.team2?.id === match.winnerId;
                                            const isCompleted = match.status === "COMPLETED";
                                            const isLive = match.status === "IN_PROGRESS" || match.status === "LIVE";

                                            return (
                                                <div
                                                    key={match.id}
                                                    className={`tournaments-section__match ${isLive ? "tournaments-section__match--live" : ""} ${isCompleted ? "tournaments-section__match--completed" : ""}`}
                                                >
                                                    <div className="tournaments-section__match-label">{round.name}</div>
                                                    <div className={`tournaments-section__match-team ${!match.team1 ? "tournaments-section__match-team--tbd" : ""} ${team1IsWinner ? "tournaments-section__match-team--winner" : ""} ${team2IsWinner && match.team1 ? "tournaments-section__match-team--loser" : ""}`}>
                                                        <span className="tournaments-section__team-seed">
                                                            {match.team1?.seed || "-"}
                                                        </span>
                                                        <span className="tournaments-section__team-name">
                                                            {match.team1?.name || "TBD"}
                                                        </span>
                                                        <span className="tournaments-section__team-score">
                                                            {match.score1 !== null ? match.score1 : "-"}
                                                        </span>
                                                    </div>
                                                    <div className={`tournaments-section__match-team ${!match.team2 ? "tournaments-section__match-team--tbd" : ""} ${team2IsWinner ? "tournaments-section__match-team--winner" : ""} ${team1IsWinner && match.team2 ? "tournaments-section__match-team--loser" : ""}`}>
                                                        <span className="tournaments-section__team-seed">
                                                            {match.team2?.seed || "-"}
                                                        </span>
                                                        <span className="tournaments-section__team-name">
                                                            {match.team2?.name || "TBD"}
                                                        </span>
                                                        <span className="tournaments-section__team-score">
                                                            {match.score2 !== null ? match.score2 : "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>)}

                                {/* Champion Column */}
                                <div className="tournaments-section__bracket-column">
                                    <div className={`tournaments-section__champion-box ${champion ? "tournaments-section__champion-box--crowned" : ""}`}>
                                        <span>{champion?.name || "TBD"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        /* No real matches yet — empty state, not a fabricated bracket. */
                        : <div className="tournaments-section__bracket-empty">
                            <FaTrophy size={24} />
                            <p>The bracket will appear here once teams are seeded and matches are scheduled.</p>
                        </div>
                    }

                    {/* Bracket Navigation - disabled until round navigation is implemented */}
                    <div className="tournaments-section__bracket-nav">
                        <button className="tournaments-section__bracket-nav-btn" disabled title="Coming soon">
                            <FaChevronLeft size={12} /> Previous Round
                        </button>
                        <button className="tournaments-section__bracket-nav-btn" disabled title="Coming soon">
                            Next Round <FaChevronRight size={12} />
                        </button>
                    </div>

                    {/* Status Legend */}
                    <div className="tournaments-section__legend">
                        <span className="tournaments-section__legend-item">
                            <span className="tournaments-section__legend-dot tournaments-section__legend-dot--upcoming" />
                            Upcoming
                        </span>
                        <span className="tournaments-section__legend-item">
                            <span className="tournaments-section__legend-dot tournaments-section__legend-dot--live" />
                            Live
                        </span>
                        <span className="tournaments-section__legend-item">
                            <span className="tournaments-section__legend-dot tournaments-section__legend-dot--completed" />
                            Completed
                        </span>
                    </div>
                </div>
            }

            {/* Teams Tab */}
            {activeTab === "teams" &&
                <div className="tournaments-section__teams-section">
                    <h2>Registered Teams</h2>
                    {isLoadingDetails
                        ? <div className="tournaments-section__teams-grid">
                            {[...Array(4)].map((_, i) =>
                                <div key={i} className="tournaments-section__team-card">
                                    <Skeleton width="100%" height={50} />
                                </div>)}
                        </div>
                        : <div className="tournaments-section__teams-grid">
                            {teams.map((team) =>
                                <div key={team.id} className="tournaments-section__team-card">
                                    <span className="tournaments-section__team-card-seed">#{team.seed || "-"}</span>
                                    <span className="tournaments-section__team-card-tag">[{team.tag || "---"}]</span>
                                    <span className="tournaments-section__team-card-name">{team.name}</span>
                                </div>)}
                            {teams.length === 0 &&
                                <p className="tournaments-section__no-teams">No teams registered yet — registration is open.</p>
                            }
                        </div>
                    }
                </div>
            }

            {/* Schedule Tab */}
            {activeTab === "schedule" &&
                <div className="tournaments-section__schedule-section">
                    <h2>Tournament Schedule</h2>
                    {useRealMatches && bracketRounds.length > 0
                        ? <div className="tournaments-section__schedule-list">
                            {bracketRounds.map((round) => {
                                // Find the earliest scheduled match in this round
                                const scheduledMatches = round.matches.filter((m) => m.scheduledTime);
                                const earliestTime = scheduledMatches.length > 0
                                    ? Math.min(...scheduledMatches.map((m) => m.scheduledTime!))
                                    : null;

                                return (
                                    <div key={round.roundNumber} className="tournaments-section__schedule-item">
                                        <span className="tournaments-section__schedule-date">
                                            {earliestTime
                                                ? new Date(earliestTime).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                                : "TBD"}
                                        </span>
                                        <span className="tournaments-section__schedule-event">
                                            {round.name} ({round.matches.length} {round.matches.length === 1 ? "match" : "matches"})
                                        </span>
                                        <span className="tournaments-section__schedule-time">
                                            {earliestTime
                                                ? new Date(earliestTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" })
                                                : "TBD"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        : <div className="tournaments-section__schedule-list">
                            <div className="tournaments-section__schedule-item">
                                <span className="tournaments-section__schedule-date">TBD</span>
                                <span className="tournaments-section__schedule-event">Quarter Finals & Semi Finals</span>
                                <span className="tournaments-section__schedule-time">TBD</span>
                            </div>
                            <div className="tournaments-section__schedule-item">
                                <span className="tournaments-section__schedule-date">TBD</span>
                                <span className="tournaments-section__schedule-event">Grand Finals</span>
                                <span className="tournaments-section__schedule-time">TBD</span>
                            </div>
                        </div>
                    }
                </div>
            }

            {/* Rules/Info Tab */}
            {activeTab === "rules" &&
                <div className="tournaments-section__rules-section">
                    <h2>Tournament Information</h2>
                    <div className="tournaments-section__rules-grid">
                        <div className="tournaments-section__rules-card">
                            <h3>Format</h3>
                            <ul>
                                <li>{displayData.format} Bracket</li>
                                <li>{displayData.teamSize}v{displayData.teamSize} Team Size</li>
                                <li>{displayData.maxTeams} Max Teams</li>
                                {bracketRounds.length > 0 &&
                                    <li>{bracketRounds.length} Rounds</li>
                                }
                            </ul>
                        </div>
                        <div className="tournaments-section__rules-card">
                            <h3>Prize Pool</h3>
                            <ul>
                                <li>Total: {displayData.prizePool}</li>
                                {featuredTournament?.prizePool?.distribution?.map((dist, idx) =>
                                    <li key={idx}>
                                        {dist.place === 1 ? "1st" : dist.place === 2 ? "2nd" : dist.place === 3 ? "3rd" : `${dist.place}th`} Place: {
                                            featuredTournament.prizePool?.currency === "USD"
                                                ? `$${((dist.amount || 0) / 100).toLocaleString()}`
                                                : `${dist.amount?.toLocaleString() || 0} ${featuredTournament.prizePool?.currency || ""}`
                                        }
                                    </li>)}
                                {!featuredTournament?.prizePool?.distribution?.length &&
                                    <li>Distribution TBD</li>
                                }
                            </ul>
                        </div>
                        <div className="tournaments-section__rules-card">
                            <h3>Details</h3>
                            <ul>
                                <li>Game: {displayData.game}</li>
                                <li>Date: {displayData.date}</li>
                                <li>Region: {displayData.region}</li>
                                <li>Platform: {displayData.platform}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
};
